<script lang="ts">
  // The fitness signature as it stood for this activity. Most activities don't move it, so the
  // ordinary case is one quiet line; a breakthrough gets the full medal treatment, because that's
  // the thing an athlete is scanning the dashboard for.
  import { cn } from '@/lib/utils';
  import type { Exercise } from '@/Common/ActivityFunctions';

  type PartialSignature = { ftp?: number; atc?: number; pp?: number };

  export let exercise: Exercise & { br_signature_changes?: PartialSignature };
  export let size: 'sm' | 'lg' = 'lg';

  type MedalStyle = { label: string; classes: string; ring: string };
  const medalStyles: Record<number, MedalStyle> = {
    [-1]: { label: 'Near Breakthrough', classes: 'bg-fake-br text-white', ring: 'border-gray-400' },
    1: { label: 'Bronze Breakthrough', classes: 'bg-bronze-br text-white', ring: 'border-[#B08D57]' },
    2: { label: 'Silver Breakthrough', classes: 'bg-silver-br text-white', ring: 'border-[#A8A9AD]' },
    3: { label: 'Gold Breakthrough', classes: 'bg-gold-br text-white', ring: 'border-[#D4AF37]' },
  };

  $: medal = medalStyles[exercise?.medal ?? 0] ?? null;
  $: signature = exercise?.signature ?? null;
  // getDashboardActivitiesData() only recomputes br_signature_changes into a real signature when
  // the activity has a medal; otherwise whatever bare array Progression left in Mongo comes through
  // as-is, with no ftp/atc/pp on it. Subtracting against that silently renders "NaN W", so require
  // a medal *and* finite numbers on both sides. The activity details page guards the same field the
  // same way -- see resources/views/activity/activity_br_changes.blade.php.
  $: previous = medal ? exercise?.br_signature_changes : null;

  const round1 = (v: number) => Math.round(v * 10) / 10;
  const delta = (current: number, before: number | undefined): number | null =>
    Number.isFinite(current) && before != null && Number.isFinite(before) ? round1(current - before) : null;
  const toKj = (joules: number | undefined): number | undefined => (joules == null ? undefined : joules / 1000);

  type SignatureStat = { key: string; value: number; unit: string; delta: number | null };
  const stat = (key: string, unit: string, current: number, before: number | undefined): SignatureStat => ({
    key,
    unit,
    value: current,
    delta: delta(current, before),
  });

  let stats: SignatureStat[] = [];
  $: stats = signature
    ? [
        stat('TP', 'W', signature.ftp, previous?.ftp),
        stat('HIE', 'kJ', signature.atc / 1000, toKj(previous?.atc)),
        stat('PP', 'W', signature.pp, previous?.pp),
      ].filter((s) => Number.isFinite(s.value)).map((s) => ({ ...s, value: round1(s.value) }))
    : [];

  // A "near breakthrough" (medal -1) confirms the signature rather than raising it, so don't
  // promise gains that aren't there.
  $: improved = stats.some((s) => s.delta != null && s.delta > 0);
  // Whether there is anything to have that opinion about. Not every source of an activity carries
  // br_signature_changes, and with no previous signature to subtract there is no way to tell a
  // confirmation from a rise -- so say neither rather than defaulting to "confirmed", which is the
  // wrong one for most medals.
  $: compared = stats.some((s) => s.delta != null);
</script>

{#if stats.length}
  {#if medal}
    <!-- Sized to its own numbers rather than to the card: a breakthrough is read as one block, and
         stretching it across a wide card means scanning empty space between the three values. -->
    <div class={cn('self-start max-w-full rounded-md border-2 border-solid overflow-hidden shadow-sm', medal.ring, size === 'lg' ? 'text-sm' : 'text-xs')}>
      <div class={cn('flex flex-row items-center justify-between gap-3 px-2.5 py-1.5 leading-none', medal.classes)}>
        <span class={cn('inline-flex items-center gap-1.5 font-semibold', size === 'lg' ? 'text-xs' : 'text-[10px]')}>
          <i class="fa-solid fa-award" aria-hidden="true"></i>{medal.label}
        </span>
        {#if compared}
          <span class={cn('opacity-90', size === 'lg' ? 'text-[11px]' : 'text-[10px]')}>
            {improved ? 'New fitness signature' : 'Signature confirmed'}
          </span>
        {/if}
      </div>
      <div class="flex flex-row divide-x divide-y-0 divide-solid divide-border bg-muted/40">
        {#each stats as entry}
          <!-- flex-1 rather than content width: the medal header can be wider than the three
               numbers put together, and the numbers would otherwise huddle at its left end. -->
          <div class="flex-1 px-3 py-1.5 text-center whitespace-nowrap">
            <div class={cn('text-muted-foreground leading-none', size === 'lg' ? 'text-[11px]' : 'text-[10px]')}>{entry.key}</div>
            <div class={cn('font-semibold leading-tight', size === 'lg' ? 'text-base' : 'text-sm')}>
              {entry.value} {entry.unit}
            </div>
            {#if entry.delta != null}
              <div class={cn('font-semibold leading-none mt-0.5', size === 'lg' ? 'text-xs' : 'text-[10px]', entry.delta >= 0 ? 'text-[#5CA907]' : 'text-[#FF2222]')}>
                <i class={cn('fa-solid mr-0.5', entry.delta >= 0 ? 'fa-caret-up' : 'fa-caret-down')} aria-hidden="true"></i>{Math.abs(entry.delta)} {entry.unit}
              </div>
            {/if}
          </div>
        {/each}
      </div>
    </div>
  {:else}
    <!-- No breakthrough: one quiet line, so the signature stops competing with the ride's own numbers. -->
    <div class="flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
      <span class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">Signature</span>
      {#each stats as entry}
        <span class="text-xs text-muted-foreground whitespace-nowrap">
          <span class="font-semibold text-foreground">{entry.value}</span> {entry.unit} {entry.key}
        </span>
      {/each}
    </div>
  {/if}
{/if}
